/**
 * Efeitos — aplica sucesso.mundo no cenario.
 *
 * D11 (inviolavel): toda tarefa muda a fazenda. O campo sucesso.mundo de cada
 * tarefa nomeia um efeito daqui. Este arquivo e a unica fonte de verdade de
 * "que efeitos existem"; o validador de conteudo cruza os JSONs com esta lista.
 *
 * D10: nenhum efeito da ponto, estrela ou moeda. Todo efeito muda o CENARIO —
 * essa e a unica recompensa que existe no jogo.
 *
 * Assinatura de cada efeito: aplicar(estado, fazenda, resultado) -> void
 *   estado    — o estado da fazenda (secao 8), alterado no lugar
 *   fazenda   — a instancia de Fazenda.js, para mexer no cenario
 *   resultado — o que o motor devolveu ao concluir a tarefa
 */

export const EFEITOS = Object.freeze({
  // --- canteiro -----------------------------------------------------------
  'canteiro.plantar': {
    fase: 'Fase 1',
    aplicar: (estado, fazenda, resultado) => {
      const cultura = resultado?.cultura ?? 'milho';
      const quantidade = resultado?.quantidade ?? 5;

      const canteiro = {
        id: estado.canteiros.length + 1,
        cultura,
        quantidade,
        agrupar_por: resultado?.agrupar_por ?? 5,
        plantado_dia: estado.dia,
        regas: 0,
        sol: true,
      };
      estado.canteiros.push(canteiro);
      fazenda.desenharPlantas(canteiro, estado.dia);
    },
  },

  'canteiro.regar': {
    fase: 'Fase 8',
    aplicar: (estado, fazenda) => {
      const canteiro = estado.canteiros.at(-1);
      if (!canteiro) return;
      canteiro.regas += 1;
      fazenda.desenharPlantas(canteiro, estado.dia);
    },
  },

  'canteiro.colher': {
    fase: 'Fase 8',
    aplicar: (estado, fazenda) => {
      const canteiro = estado.canteiros.pop();
      if (!canteiro) return;
      const grupo = fazenda.plantas.get(canteiro.id);
      if (grupo) {
        fazenda.raiz.remove(grupo);
        fazenda.plantas.delete(canteiro.id);
      }
    },
  },

  // --- construcoes --------------------------------------------------------
  // Ordem de liberacao da secao 7:
  // cerca -> espantalho -> galinheiro -> poco -> composteira -> moinho ->
  // estufa -> roda d'agua -> ponte
  'cerca.construir': construir('cerca', 'Fase 3'),
  'galinheiro.construir': construir('galinheiro', 'Fase 8'),
  'poco.construir': construir('poco', 'Fase 8'),
  'composteira.construir': construir('composteira', 'Fase 8'),
  'moinho.construir': construir('moinho', 'Fase 8'),
  'estufa.construir': construir('estufa', 'Fase 8'),
  'roda_dagua.construir': construir('roda_dagua', 'Fase 8'),
  'ponte.construir': construir('ponte', 'Fase 8'),

  // --- celeiro ------------------------------------------------------------
  'celeiro.guardar': {
    fase: 'Fase 3',
    aplicar: (estado, fazenda, resultado) => {
      // Guardar sementes separadas deixa o celeiro organizado: sobe de nivel.
      const atual = estado.construcoes.celeiro ?? { nivel: 0, dia: estado.dia };
      atual.nivel += 1;
      atual.dia = estado.dia;
      estado.construcoes.celeiro = atual;

      if (resultado?.colecionavel) {
        estado.colecao.push(resultado.colecionavel);
      }
      fazenda.acordarAncora('celeiro');
    },
  },

  // --- galinheiro ---------------------------------------------------------
  'galinheiro.alimentar': {
    fase: 'Fase 8',
    aplicar: (estado) => {
      for (const animal of estado.animais) {
        animal.alimentado_dia = estado.dia;
      }
      if (estado.animais.length === 0) {
        estado.animais.push({ id: 'gal_1', tipo: 'galinha', alimentado_dia: estado.dia });
      }
    },
  },

  // --- composteira --------------------------------------------------------
  'composteira.separar': {
    fase: 'Fase 8',
    aplicar: (estado) => {
      const atual = estado.construcoes.composteira ?? { nivel: 0, dia: estado.dia };
      atual.nivel += 1;
      atual.dia = estado.dia;
      estado.construcoes.composteira = atual;
    },
  },

  // --- caderno (palpites) -------------------------------------------------
  'caderno.anotar': {
    fase: 'Fase 8',
    aplicar: (estado, fazenda, resultado) => {
      estado.caderno.push({
        pergunta: resultado?.pergunta ?? 'sem_pergunta',
        palpite: resultado?.palpite ?? null,
        dia: estado.dia,
        resolvido: false,
      });
    },
  },

  // --- cozinha ------------------------------------------------------------
  'cozinha.dividir': {
    fase: 'Fase 8',
    aplicar: (estado) => {
      const atual = estado.construcoes.cozinha ?? { nivel: 0, dia: estado.dia };
      atual.nivel += 1;
      atual.dia = estado.dia;
      estado.construcoes.cozinha = atual;
    },
  },
});

/** Fabrica dos efeitos de construcao — todos fazem a mesma coisa. */
function construir(nome, fase) {
  return {
    fase,
    aplicar: (estado, fazenda) => {
      const atual = estado.construcoes[nome];
      estado.construcoes[nome] = {
        nivel: (atual?.nivel ?? 0) + 1,
        dia: estado.dia,
      };
      fazenda.acordarAncora(nome);
    },
  };
}

/** Nomes de todos os efeitos declarados. */
export function nomesDeEfeitos() {
  return Object.keys(EFEITOS);
}

/** true se o efeito ja muda o cenario de verdade. */
export function efeitoImplementado(nome) {
  return Boolean(EFEITOS[nome]?.aplicar);
}

/**
 * Aplica um efeito no cenario.
 * Falha alto e em portugues se o efeito nao existir — sem catch silencioso (P5).
 */
export function aplicarEfeito(nome, estado, fazenda, resultado) {
  const efeito = EFEITOS[nome];

  if (!efeito) {
    throw new Error(
      `FAZENDA: o efeito "${nome}" nao existe em src/world/Efeitos.js. ` +
        `Efeitos declarados: ${nomesDeEfeitos().join(', ')}. ` +
        'Confira o campo "sucesso.mundo" do JSON da tarefa.'
    );
  }

  if (!efeito.aplicar) {
    throw new Error(
      `FAZENDA: o efeito "${nome}" esta declarado mas ainda nao foi implementado. ` +
        `Ele entra na ${efeito.fase} (secao 13 da ESPECIFICACAO.md).`
    );
  }

  efeito.aplicar(estado, fazenda, resultado);
  console.info(`FAZENDA: efeito "${nome}" aplicado. A fazenda mudou.`);
}
